import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ThemeContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load user's theme preference from localStorage first, then from user object
  useEffect(() => {
    // Try localStorage first for immediate response
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      setDarkMode(savedMode === 'true');
    } else if (user) {
      setDarkMode(user.dark_mode || false);
    }
    setLoading(false);
  }, [user]);

  // Apply theme class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleDarkMode = async () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    
    // Save to localStorage for immediate persistence
    localStorage.setItem('darkMode', newValue.toString());
    
    // Also save to backend if user is logged in
    if (user?.sub) {
      try {
        await axios.put(`${API}/users/${user.sub}/dark-mode`, {
          dark_mode: newValue
        });
      } catch (error) {
        console.error('Failed to save dark mode preference:', error);
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
